/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.allowWholesaleVariants = this.getWholesaleContext();
      this.wholesaleOptionIndexes = this.getWholesaleOptionIndexes();

      if (!this.allowWholesaleVariants) {
        this.filterWholesaleVariants();
      }

      const initialSelectionChanged = this.syncInitialSelection();

      this.updateAvailability();
      if (initialSelectionChanged && this.variant) {
        this.updateVariantInput();
        this.updateAddToCartButton();
        this.updatePrice();
        this.updateBackorderText();
        this.updatePickupAvailability();
        this.updateSku();
      }
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Checks whether the current picker should expose wholesale-only variants.
     * @returns {Boolean}
     */
    getWholesaleContext() {
      const inWholesaleDrawer = this.closest('[data-wholesale-context="true"]');
      const params = new URLSearchParams(window.location.search);

      return Boolean(inWholesaleDrawer || params.get('wholesale') === '1');
    }

    /**
     * Identifies native variants/options reserved for box/carton wholesale pricing.
     * @param {String} value - Variant title or option value.
     * @returns {Boolean}
     */
    static isWholesaleVariantValue(value) {
      const normalized = String(value || '').trim().toLowerCase();

      return /^(box|carton)(\s+of)?\s+\d+/.test(normalized);
    }

    /**
     * Returns option positions used only to expose native wholesale box variants.
     * @returns {Set}
     */
    getWholesaleOptionIndexes() {
      const indexes = new Set();

      this.data.product.variants.forEach((variant) => {
        variant.options.forEach((option, index) => {
          if (VariantPicker.isWholesaleVariantValue(option)) {
            indexes.add(index);
          }
        });
      });

      return indexes;
    }

    /**
     * Removes wholesale-only variants from public product data and controls.
     */
    filterWholesaleVariants() {
      const publicVariants = this.data.product.variants.filter((variant) => {
        const values = [variant.title, ...variant.options];
        return !values.some(VariantPicker.isWholesaleVariantValue);
      });

      if (!publicVariants.length) return;

      this.data.product.variants = publicVariants;
      this.removeFilteredOptions();
    }

    /**
     * Removes option controls that can no longer form a public variant.
     */
    removeFilteredOptions() {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        const allowedValues = new Set(
          this.data.product.variants.map((variant) => variant.options[selectorIndex])
        );

        if (!allowedValues.size) return;

        if (selector.dataset.selectorType === 'dropdown') {
          selector.querySelectorAll('.custom-select__option.js-option').forEach((optionEl) => {
            if (!allowedValues.has(optionEl.dataset.value)) {
              optionEl.remove();
            }
          });

          const btnText = selector.querySelector('.custom-select__btn .text-start');
          const selectedOption = selector.querySelector('.custom-select__option[aria-selected="true"]');

          if (!selectedOption && btnText) {
            const firstOption = selector.querySelector('.custom-select__option.js-option');
            if (firstOption) {
              firstOption.setAttribute('aria-selected', 'true');
              btnText.textContent = firstOption.textContent.trim();
              selector.querySelector('.custom-select__listbox')?.setAttribute(
                'aria-activedescendant',
                firstOption.id
              );
            }
          }

          return;
        }

        selector.querySelectorAll('input.js-option').forEach((input) => {
          if (!allowedValues.has(input.value)) {
            input.nextElementSibling?.remove();
            input.remove();
          }
        });

        if (!selector.querySelector('input.js-option:checked')) {
          const firstInput = selector.querySelector('input.js-option');
          if (firstInput) firstInput.checked = true;
        }
      });

      this.hideFilteredWholesaleSelectors();
      this.optionSelectors = this.querySelectorAll('.option-selector');
    }

    /**
     * Keeps the Unit value selected while hiding the public-only Purchase Type UI.
     */
    hideFilteredWholesaleSelectors() {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        if (!this.wholesaleOptionIndexes.has(selectorIndex)) return;

        const values = new Set(
          Array.from(selector.querySelectorAll('.js-option')).map((optionEl) => {
            if (selector.dataset.selectorType === 'dropdown') return optionEl.dataset.value;
            return optionEl.value;
          })
        );

        if (values.size <= 1) {
          selector.hidden = true;
          selector.setAttribute('aria-hidden', 'true');
        }
      });

      const productOptions = this.closest('.product-options');
      if (!productOptions) return;

      const visibleSelectors = Array.from(productOptions.querySelectorAll('.option-selector'))
        .filter((selector) => !selector.hidden);

      productOptions.hidden = visibleSelectors.length === 0;
    }

    /**
     * Aligns selected controls to a valid public variant after filtering.
     * @returns {Boolean}
     */
    syncInitialSelection() {
      const selectedOptions = this.getSelectedOptions();
      let variant = this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index])
      );

      if (variant) {
        this.variant = variant;
        return false;
      }

      variant = this.data.product.variants[0];
      if (!variant) return false;

      this.setSelectedOptions(variant.options);
      this.variant = variant;

      return true;
    }

    /**
     * Selects picker controls by option values.
     * @param {Array} options - Variant option values.
     */
    setSelectedOptions(options) {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        const value = options[selectorIndex];

        if (selector.dataset.selectorType === 'dropdown') {
          const optionEl = Array.from(selector.querySelectorAll('.custom-select__option.js-option'))
            .find((option) => option.dataset.value === value);
          const btnText = selector.querySelector('.custom-select__btn .text-start');

          selector.querySelectorAll('.custom-select__option[aria-selected="true"]')
            .forEach((option) => option.removeAttribute('aria-selected'));

          if (optionEl) {
            optionEl.setAttribute('aria-selected', 'true');
            if (btnText) btnText.textContent = optionEl.textContent.trim();
            selector.querySelector('.custom-select__listbox')?.setAttribute(
              'aria-activedescendant',
              optionEl.id
            );
          }

          return;
        }

        const input = Array.from(selector.querySelectorAll('input.js-option'))
          .find((option) => option.value === value);

        if (input) input.checked = true;
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const selectedOptions = this.getSelectedOptions();
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index])
      );

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.getSelectedOptions() };
      }

      const updateOptionAvailability = (optionEl, available, soldout) => {
        const el = optionEl;
        const text = soldout ? theme.strings.noStock : theme.strings.noVariant;
        el.classList.toggle('is-unavailable', !available);

        if (optionEl.classList.contains('custom-select__option')) {
          const em = el.querySelector('em');

          if (em) {
            em.hidden = available;
          }

          if (!available) {
            if (em) {
              em.textContent = text;
            } else {
              el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${text}</em>`;
            }
          }
        } else if (!available) {
          el.nextElementSibling.title = text;
        } else {
          el.nextElementSibling.removeAttribute('title');
        }
      };

      // Flag all options as unavailable
      this.querySelectorAll('.js-option').forEach((optionEl) => {
        updateOptionAvailability(optionEl, false, false);
      });

      // Flag selector options as available or sold out, depending on the variant availability
      this.optionSelectors.forEach((selector, selectorIndex) => {
        this.data.product.variants.forEach((variant) => {
          let matchCount = 0;

          variant.options.forEach((option, optionIndex) => {
            if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
              matchCount += 1;
            }
          });

          if (matchCount === currVariant.options.length - 1) {
            const options = selector.querySelectorAll('.js-option');
            const optionEl = Array.from(options).find((opt) => {
              if (selector.dataset.selectorType === 'dropdown') {
                return opt.firstElementChild.textContent === variant.options[selectorIndex];
              }
              return (
                opt.nextElementSibling.querySelector('.js-value').textContent ===
                variant.options[selectorIndex]
              );
            });

            if (optionEl) {
              updateOptionAvailability(optionEl, variant.available, !variant.available);
            }
          }
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    // updatePrice() {
    //   this.price = this.price || this.section.querySelector('.product-info__price > .price');
    //   if (!this.price) return;

    //   if (this.variant) {
    //     const priceCurrentEl = this.price.querySelector('.price__current');
    //     const priceWasEl = this.price.querySelector('.price__was');
    //     const unitPriceEl = this.price.querySelector('.unit-price');

    //     // Update current price and original price if on sale.
    //     priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
    //     if (priceWasEl)
    //       priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

    //     // Update unit price, if specified.
    //     if (this.variant.unit_price_measurement) {
    //       const valueEl = this.price.querySelector('.unit-price__price');
    //       const unitEl = this.price.querySelector('.unit-price__unit');
    //       const value = this.variant.unit_price_measurement.reference_value;
    //       const unit = this.variant.unit_price_measurement.reference_unit;

    //       valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
    //       unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
    //     }

    //     unitPriceEl.hidden = !this.variant.unit_price_measurement;
    //     this.price.classList.toggle(
    //       'price--on-sale',
    //       this.variant.compare_at_price > this.variant.price
    //     );
    //     this.price.classList.toggle('price--sold-out', !this.variant.available);
    //   }

    //   this.price.querySelector('.price__default').hidden = !this.variant;
    //   this.price.querySelector('.price__no-variant').hidden = this.variant;
    // }

    formatMoney(cents) {
      if (!window.theme?.settings?.moneyWithCurrencyFormat) {
        return (cents / 100).toFixed(2);
      }

      const format = theme.settings.moneyWithCurrencyFormat;
      const formatWithDelimiters = (
        value,
        precision = 2,
        thousands = ',',
        decimal = '.',
        stripTrailingZeros = false
      ) => {
        if (Number.isNaN(value) || value === null) return '0';

        const fixedValue = (value / 100).toFixed(precision);
        const parts = fixedValue.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsValue = parts[1];

        if (!centsValue || (stripTrailingZeros && parseInt(centsValue, 10) === 0)) {
          return dollars;
        }

        return `${dollars}${decimal}${centsValue}`;
      };

      const placeholderFormatters = {
        amount: () => formatWithDelimiters(cents),
        amount_no_decimals: () => formatWithDelimiters(cents, 0),
        amount_with_comma_separator: () => formatWithDelimiters(cents, 2, '.', ','),
        amount_with_space_separator: () => formatWithDelimiters(cents, 2, ' ', ','),
        amount_with_period_and_space_separator: () => formatWithDelimiters(cents, 2, ' ', '.'),
        amount_no_decimals_with_comma_separator: () => formatWithDelimiters(cents, 0, '.', ','),
        amount_no_decimals_with_space_separator: () => formatWithDelimiters(cents, 0, ' '),
        amount_with_apostrophe_separator: () => formatWithDelimiters(cents, 2, "'", '.'),
        amount_with_currency: () => formatWithDelimiters(cents)
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
        const formatter = placeholderFormatters[placeholder] || placeholderFormatters.amount;
        return formatter();
      });
    }


    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price || !this.variant) return;

      const priceCurrentEl = this.price.querySelector('.price__current');
      const priceWasEl = this.price.querySelector('.price__was');
      const unitPriceEl = this.price.querySelector('.unit-price');

      const price = this.variant.price;
      const compareAtPrice = this.variant.compare_at_price;

      // === CURRENT PRICE ===
      priceCurrentEl.innerHTML = this.formatMoney(price);

      // === COMPARE PRICE (ONLY IF VALID) ===
      if (compareAtPrice && compareAtPrice > price) {
        priceWasEl.innerHTML = this.formatMoney(compareAtPrice);
        priceWasEl.hidden = false;
        this.price.classList.add('price--on-sale');
      } else {
        priceWasEl.innerHTML = '';
        priceWasEl.hidden = true;
        this.price.classList.remove('price--on-sale');
      }

      // === UNIT PRICE (discounted) ===
      if (this.variant.unit_price_measurement) {
        const valueEl = this.price.querySelector('.unit-price__price');
        const unitEl = this.price.querySelector('.unit-price__unit');

        valueEl.innerHTML = this.formatMoney(this.variant.unit_price);

        const value = this.variant.unit_price_measurement.reference_value;
        const unit = this.variant.unit_price_measurement.reference_unit;
        unitEl.textContent = value === 1 ? unit : `${value}${unit}`;

        unitPriceEl.hidden = false;
      } else {
        unitPriceEl.hidden = true;
      }

      // === AVAILABILITY ===
      this.price.classList.toggle('price--sold-out', !this.variant.available);

      // === STATE VISIBILITY ===
      this.price.querySelector('.price__default').hidden = false;
      this.price.querySelector('.price__no-variant').hidden = true;
    }


    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
